import { useState, type RefObject } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useTheme } from "next-themes";

import { useLanguage } from "@/hooks/useLanguage";
import type { CaptchaMode } from "@/lib/captcha";

const copy = {
  cs: {
    unavailable: "Bezpečnostní kontrola na této adrese nefunguje.",
    continueOn: "Pokračovat na",
    failed: "Bezpečnostní kontrolu se nepodařilo načíst. Obnovte stránku.",
    generic: "Otevřete náhled přes adresu pr-<číslo>.zettlapp.com uvedenou v pull requestu.",
  },
  en: {
    unavailable: "The security check doesn't work on this address.",
    continueOn: "Continue on",
    failed: "The security check couldn't load. Reload the page to try again.",
    generic:
      "Open this preview through its pr-<number>.zettlapp.com address from the pull request.",
  },
};

interface CaptchaFieldProps {
  mode: CaptchaMode;
  widgetRef: RefObject<TurnstileInstance>;
  onToken: (token: string | null) => void;
}

export function CaptchaField({ mode, widgetRef, onToken }: CaptchaFieldProps) {
  const { language } = useLanguage();
  const { resolvedTheme } = useTheme();
  // Turnstile's error code, e.g. 110200 when the widget doesn't list this hostname.
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const c = copy[language];

  if (mode.kind === "off") return null;

  if (mode.kind === "hint") {
    return (
      <p role="note" className="text-sm text-muted-foreground">
        {c.unavailable}{" "}
        {mode.href ? (
          <a href={mode.href} className="font-medium underline underline-offset-4">
            {c.continueOn} {new URL(mode.href).host}
          </a>
        ) : (
          c.generic
        )}
      </p>
    );
  }

  const clear = () => onToken(null);
  return (
    <div>
      <Turnstile
        ref={widgetRef}
        siteKey={mode.siteKey}
        options={{ theme: resolvedTheme === "dark" ? "dark" : "light", language, size: "flexible" }}
        onSuccess={(token) => {
          setErrorCode(null);
          onToken(token);
        }}
        // A theme or language switch re-renders the widget; its old token goes with it.
        onWidgetLoad={clear}
        onExpire={clear}
        onError={(code) => {
          setErrorCode(String(code));
          clear();
        }}
        onTimeout={clear}
      />
      {errorCode && (
        <p role="alert" className="text-sm text-red-500 dark:text-red-400">
          {c.failed} ({errorCode})
        </p>
      )}
    </div>
  );
}
